<template>
  <SoWidget data-testid="warrants-summary-card">
    <template #title>
      <div class="d-flex align-center justify-space-between w-100 gap-4">
        <h6 class="font-weight-medium">Communications summary</h6>
        <SoDatePicker
          v-model="dateRange"
          type="daterange"
          label="Date range"
          data-testid="warrants-summary-date-picker"
        />
      </div>
    </template>

    <SoAlert
      v-if="isError"
      type="error"
      title="Failed to load communications summary"
      data-testid="warrants-summary-error"
    />
    <SoSummaryTable
      v-else
      :items="items"
      :loading="isLoading"
      :aria-headers="['Metric', 'Value']"
    />
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import SoWidget from "@/components/SoWidget.vue"
  import SoSummaryTable from "@/components/SoSummaryTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import { SoSummaryTableItem } from "@/types/components/SoSummaryTable"
  import { QueryResult } from "@/api/utils/types"
  import { WarrantsSummaryService } from "@/services/admin/WarrantsSummaryService"
  import { IWarrantsCompletionSummary } from "@/models/admin/warrantsCompletionSummary/warrantsCompletionSummary.types"

  // Warrant journeys span weeks, so default to a window wide enough to be useful.
  const DEFAULT_RANGE_DAYS = 90

  const EMPTY_SUMMARY: IWarrantsCompletionSummary = {
    totalCommunications: 0,
    totalCompleted: 0,
    averageDaysToComplete: null,
  }

  export default defineComponent({
    name: "WarrantsSummaryCard",
    components: {
      SoWidget,
      SoSummaryTable,
      SoAlert,
      SoDatePicker,
    },
    data() {
      return {
        dateRange: [
          dayjs().subtract(DEFAULT_RANGE_DAYS, "day").toDate(),
          dayjs().toDate(),
        ] as Date[] | null,
        summaryQuery: null as QueryResult<IWarrantsCompletionSummary> | null,
      }
    },
    computed: {
      fromIso(): string | undefined {
        if (!this.dateRange || this.dateRange.length < 2) return undefined
        return dayjs(this.dateRange[0]).startOf("day").toISOString()
      },
      toIso(): string | undefined {
        if (!this.dateRange || this.dateRange.length < 2) return undefined
        return dayjs(this.dateRange[1]).endOf("day").toISOString()
      },
      isLoading(): boolean {
        return Boolean(this.summaryQuery?.isFetching)
      },
      isError(): boolean {
        return Boolean(this.summaryQuery?.isError)
      },
      summaryData(): IWarrantsCompletionSummary {
        return this.summaryQuery?.data ?? EMPTY_SUMMARY
      },
      averageDisplay(): string | null {
        const average = this.summaryData.averageDaysToComplete
        if (average === null) return null
        // One decimal place is enough precision for an average measured in days.
        return `${Math.round(average * 10) / 10} days`
      },
      items(): SoSummaryTableItem[] {
        const { totalCommunications, totalCompleted } = this.summaryData
        return [
          { title: "Total communications", value: totalCommunications },
          { title: "Total completed", value: totalCompleted },
          {
            title: "Average time to completed",
            value: this.averageDisplay,
            fallbackValue: "—",
          },
        ]
      },
    },
    watch: {
      dateRange() {
        this.runQuery()
      },
    },
    mounted() {
      this.runQuery()
    },
    methods: {
      runQuery() {
        this.summaryQuery = WarrantsSummaryService.getCompletionSummary(
          this.fromIso,
          this.toIso,
        )
      },
    },
  })
</script>
